import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CookiePreferencesModal from './CookiePreferencesModal';
import { 
  COOKIE_CONSENT_KEY, 
  COOKIE_PREFERENCES_KEY,
  initializeCookieFeatures, 
  clearNonEssentialCookies,
  dispatchCookiePreferenceChange
} from '../utils/cookieManager';
import { initializeAnalytics } from '../utils/analytics';
import './CookieConsentBanner.css'; // Import external CSS instead of inline styles

function CookieConsentBanner({ countryCode }) {
  // Initialize with null to prevent initial render until we check localStorage
  // This prevents the banner from being the LCP element during initial load
  const [isVisible, setIsVisible] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback to defer banner loading until browser is idle
    // This prevents blocking the main thread during critical rendering
    const checkConsent = () => {
      const consentAccepted = localStorage.getItem(COOKIE_CONSENT_KEY);
      setIsVisible(!consentAccepted);
      setIsLoaded(true);
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(checkConsent, { timeout: 1000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(checkConsent, 100);
    }
  }, []);

  const handleAccept = async () => {
    // Set consent and save "Accept All" preferences
    const allAcceptedPreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAcceptedPreferences));
    setIsVisible(false);
    
    // Notify components of the preference change
    dispatchCookiePreferenceChange();
    
    // Initialize all cookie features with the accepted preferences
    setTimeout(async () => {
      initializeCookieFeatures();
      // Initialize Google Analytics since user accepted all cookies
      try {
        await initializeAnalytics();
        console.log('Analytics initialized after user accepted all cookies');
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
      }
    }, 100);
  };

  const handleManagePreferences = (e) => {
    e.preventDefault();
    setShowPreferences(true);
  };

  const handlePreferencesSave = (preferences) => {
    setIsVisible(false);
    // Notify components of the preference change
    dispatchCookiePreferenceChange();
    // Clear cookies that are no longer allowed
    clearNonEssentialCookies();
    // Initialize features based on new preferences
    setTimeout(() => {
      initializeCookieFeatures();
    }, 100);
    console.log('Cookie preferences saved:', preferences);
  };

  // Return null immediately if not loaded or not visible
  // This prevents any DOM rendering until we know the banner should show
  if (!isLoaded || isVisible === null || !isVisible) {
    return null;
  }

  const privacyPolicyPath = countryCode ? `/country/${countryCode}/privacy` : '/privacy';

  return (
    <div 
      className="cookie-consent-banner" 
      role="banner" 
      aria-label="Cookie consent notice"
    >
      <noscript>
        <div>
          <p>This site uses cookies. For more information on our cookie usage and to manage your preferences, please visit our Privacy Policy. You can contact us at <a href="mailto:admin@originselect.com">admin@originselect.com</a> for any queries.</p>
        </div>
      </noscript>
      <div className="cookie-consent-content">
        <p className="cookie-consent-text">
          We use cookies to enhance your browsing experience and analyze site traffic. 
          By clicking "Accept All", you consent to our use of cookies. 
          Read our <Link to={privacyPolicyPath} className="cookie-consent-link">Privacy Policy</Link> for more information or{' '}
          <button 
            onClick={handleManagePreferences}
            className="cookie-consent-link cookie-preferences-button"
          >
            Manage Preferences
          </button>.
        </p>
        <button 
          onClick={handleAccept} 
          className="cookie-consent-button"
          aria-label="Accept all cookies"
        >
          Accept All
        </button>
      </div>
      
      <CookiePreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handlePreferencesSave}
      />
    </div>
  );
}

export default CookieConsentBanner;
